<template>
  <div class="py-2 px-3 mb-3 last:mb-0 rounded-[8px]">
    <div class="flex items-center">
      <h4 class="text-block-title flex items-center m-0 w-full error">
        <div v-if="isAttributeTypeCheckbox" class="checkbox-wrap">
          <input
            v-model="editedValue"
            class="checkbox !rtl:ml-2 !rtl:mr-0"
            type="checkbox"
            @change="onUpdate"
          />
        </div>
        <div
          class="flex items-center justify-between w-full"
          :class="{
            'list-type': isAttributeTypeList,
            'checkbox-type': isAttributeTypeCheckbox,
            'link-type': isAttributeTypeLink,
          }"
        >
          <span
            class="attribute-name"
            :class="{ error: $v.editedValue.$error }"
          >
            {{ label }}
          </span>
          <div v-if="notAttributeTypeCheckboxAndList" class="flex gap-1 mr-1">
            <button
              v-tooltip.right="$t('CUSTOM_ATTRIBUTES.ACTIONS.EDIT')"
              variant="link"
              size="small"
              color-scheme="secondary"
              icon="edit"
              class-names="edit-button"
              type="button"
              @click="onEdit"
            >
              <span
                class="material-symbols-outlined text-neutral-500 text-[17px] cursor-pointer"
              >
                edit
              </span>
            </button>
            <button
              v-tooltip="$t('CUSTOM_ATTRIBUTES.ACTIONS.COPY')"
              variant="link"
              size="small"
              color-scheme="secondary"
              icon="clipboard"
              class-names="edit-button"
              type="button"
              @click="onCopy"
            >
              <span
                class="material-symbols-outlined text-neutral-500 text-[17px] cursor-pointer"
              >
                content_copy
              </span>
            </button>
          </div>
          <button
            v-if="showActions"
            v-tooltip.left="$t('CUSTOM_ATTRIBUTES.ACTIONS.DELETE')"
            icon="delete"
            class-names="flex justify-end w-4"
            type="button"
            @click="onDelete"
          >
            <span
              class="material-symbols-outlined text-neutral-500 text-[20px] cursor-pointer"
            >
              delete
            </span>
          </button>
        </div>
      </h4>
    </div>
    <div v-if="notAttributeTypeCheckboxAndList">
      <div v-show="isEditing">
        <div class="flex flex-row mt-2 mb-2 items-stretch">
          <input
            ref="inputfield"
            v-model="editedValue"
            :type="inputType"
            class="rounded-l-[8px] rounded-r-none rtl:rounded-r-[8px] rtl:rounded-l-none mb-0 flex-1 hover:border-[#9CA3AF] focus:border-[#9CA3AF]"
            autofocus="true"
            :class="{ error: $v.editedValue.$error }"
            @blur="$v.editedValue.$touch"
            @keyup.enter="onUpdate"
          />
          <button
            class="rounded-r-[8px] rounded-l-none rtl:rounded-l-[8px] rtl:rounded-r-none self-stretch w-[40px] bg-green-500"
            type="button"
            @click="onUpdate"
          >
            <span class="material-symbols-outlined text-white text-[20px]">
              check
            </span>
          </button>
        </div>
        <span
          v-if="shouldShowErrorMessage"
          class="text-red-400 dark:text-red-500 text-sm block font-normal -mt-px w-full"
        >
          {{ errorMessage }}
        </span>
      </div>
      <div
        v-show="!isEditing"
        class="value--view"
        :class="{ 'is-editable': showActions }"
      >
        <a
          v-if="isAttributeTypeLink"
          :href="hrefURL"
          target="_blank"
          rel="noopener noreferrer"
          class="value inline-block rounded-sm mb-0 mt-2 !p-0"
        >
          {{ urlValue }}
        </a>
        <p
          v-else
          class="value inline-block rounded-sm mb-0 mt-2 break-all py-0.5 px-1"
        >
          {{ displayValue || '---' }}
        </p>
      </div>
    </div>
    <div v-if="isAttributeTypeList" class="mt-2">
      <woot-multiselect
        v-model="onListSelected"
        :options="listOptions"
        :show-labels="false"
        placeholder="None"
        track-by="id"
        label="name"
        class="no-margin"
      />
    </div>
  </div>
</template>

<script>
import { format, parseISO } from 'date-fns';
import { required, url } from 'vuelidate/lib/validators';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { isValidURL } from 'dashboard/helper/URLHelper.js';
const DATE_FORMAT = 'yyyy-MM-dd';

export default {
  props: {
    label: { type: String, required: true },
    values: { type: Array, default: () => [] },
    value: { type: [String, Number, Boolean], default: '' },
    showActions: { type: Boolean, default: false },
    attributeType: { type: String, default: 'text' },
    attributeKey: { type: String, required: true },
    contactId: { type: Number, default: null },
  },
  data() {
    return {
      isEditing: false,
      editedValue: null,
    };
  },

  computed: {
    displayValue() {
      if (this.isAttributeTypeDate) {
        return new Date(this.value || new Date()).toLocaleDateString();
      }
      if (this.isAttributeTypeCheckbox) {
        return this.value === 'false' ? false : this.value;
      }
      return this.value;
    },
    formattedValue() {
      return this.isAttributeTypeDate
        ? format(this.value ? new Date(this.value) : new Date(), DATE_FORMAT)
        : this.value;
    },
    listOptions() {
      return this.values.map((value, index) => ({
        id: index + 1,
        name: value,
      }));
    },
    selectedItem() {
      const id = this.values.indexOf(this.editedValue) + 1;
      return { id, name: this.editedValue };
    },
    isAttributeTypeCheckbox() {
      return this.attributeType === 'checkbox';
    },
    isAttributeTypeList() {
      return this.attributeType === 'list';
    },
    isAttributeTypeLink() {
      return this.attributeType === 'link';
    },
    isAttributeTypeDate() {
      return this.attributeType === 'date';
    },
    urlValue() {
      return isValidURL(this.value) ? this.value : '---';
    },
    hrefURL() {
      return isValidURL(this.value) ? this.value : '';
    },
    notAttributeTypeCheckboxAndList() {
      return !this.isAttributeTypeCheckbox && !this.isAttributeTypeList;
    },
    inputType() {
      return this.isAttributeTypeLink ? 'url' : this.attributeType;
    },
    shouldShowErrorMessage() {
      return this.$v.editedValue.$error;
    },
    errorMessage() {
      if (this.$v.editedValue.url) {
        return this.$t('CUSTOM_ATTRIBUTES.VALIDATIONS.INVALID_URL');
      }
      return this.$t('CUSTOM_ATTRIBUTES.VALIDATIONS.REQUIRED');
    },
    onListSelected: {
      get() {
        return this.listOptions.find(i => i.name === this.editedValue);
      },
      set(value) {
        this.editedValue = value.name;
        this.onUpdate();
      },
    },
  },
  watch: {
    value() {
      this.isEditing = false;
      this.editedValue = this.formattedValue;
    },
  },

  validations() {
    if (this.isAttributeTypeLink) {
      return {
        editedValue: { required, url },
      };
    }
    return {
      editedValue: { required },
    };
  },
  mounted() {
    this.editedValue = this.formattedValue;
    bus.$on(BUS_EVENTS.FOCUS_CUSTOM_ATTRIBUTE, this.onFocusAttribute);
  },
  destroyed() {
    bus.$off(BUS_EVENTS.FOCUS_CUSTOM_ATTRIBUTE, this.onFocusAttribute);
  },
  methods: {
    onFocusAttribute(focusAttributeKey) {
      if (this.attributeKey === focusAttributeKey) {
        this.onEdit();
      }
    },
    focusInput() {
      if (this.$refs.inputfield) {
        this.$refs.inputfield.focus();
      }
    },
    onEdit() {
      this.isEditing = true;
      this.$nextTick(() => {
        this.focusInput();
      });
    },
    onUpdate() {
      const updatedValue =
        this.attributeType === 'date'
          ? parseISO(this.editedValue)
          : this.editedValue;
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }
      this.isEditing = false;
      this.$emit('update', this.attributeKey, updatedValue);
    },
    onDelete() {
      this.isEditing = false;
      this.$emit('delete', this.attributeKey);
    },
    onCopy() {
      this.$emit('copy', this.value);
    },
  },
};
</script>

<style lang="scss" scoped>
.checkbox-wrap {
  @apply flex items-center;
}
.checkbox {
  appearance: none;
  @apply rounded-[4px] border-gray-300 m-0 w-[17px] h-[17px] cursor-pointer border relative bg-white mr-2;

  &:checked {
    @apply bg-green-500 border-green-500;

    &::after {
      content: '';
      position: absolute;
      left: 5px;
      top: 2px;
      width: 5px;
      height: 9px;
      border: 2px solid #fff;
      border-top: none;
      border-left: none;
      transform: rotate(45deg);
    }
  }
}
.attribute-name {
  @apply w-full text-sm text-gray-700 dark:text-slate-100;
  &.error {
    @apply text-red-400 dark:text-red-500;
  }
}

.list-type {
  .attribute-name {
    @apply w-full text-gray-700 dark:text-slate-100 mb-0.5;
    &.error {
      @apply text-red-400 dark:text-red-500;
    }
  }
}

.edit-button {
  @apply hidden;
}

.value--view {
  @apply flex;

  &.is-editable:hover {
    .value {
      @apply bg-slate-50 dark:bg-slate-700 mb-0;
    }
    .edit-button {
      @apply block;
    }
  }
}

::v-deep {
  .selector-wrap {
    @apply m-0 top-1;
    .selector-name {
      @apply ml-0;
    }
  }
  .name {
    @apply ml-0;
  }
}
</style>
